/**
 * DonorTimeline.jsx — Phase 5
 * Unified activity timeline per donor — shows comms, KPI changes, grants.
 * Also surfaces alerts: KPI at-risk, renewal due, relationship lapse.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  getDonors, getDonorGrants, getDonorComms,
  getDonorContacts, kpiRiskLevel, daysUntil, fmtDate, fmtDateTime,
} from '../../../api/donorsApi';

// ── Alert panel ───────────────────────────────────────────────────────────────
function AlertsPanel({ grants, contacts }) {
  const alerts = [];

  // KPI alerts
  grants.forEach(g => {
    (g.kpis || []).forEach(kpi => {
      const risk = kpiRiskLevel(kpi);
      if (risk !== 'on-track') {
        alerts.push({
          type: risk === 'critical' ? 'danger' : 'warning',
          icon: '⚠',
          text: `KPI "${kpi.label}" on grant "${g.title}" is ${risk.replace('-', ' ')} (${kpi.current}/${kpi.target} ${kpi.unit})`,
        });
      }
    });
    // Renewal alerts
    if (g.renewalDate) {
      const days = daysUntil({ toDate: () => new Date(g.renewalDate) });
      if (days !== null && days <= 60 && days >= 0) {
        alerts.push({
          type: days <= 14 ? 'danger' : 'warning',
          icon: '🔁',
          text: `Grant "${g.title}" renewal is due in ${days} day${days !== 1 ? 's' : ''}`,
        });
      }
      if (g.renewalDate && daysUntil({ toDate: () => new Date(g.renewalDate) }) < 0) {
        alerts.push({ type: 'danger', icon: '🔁', text: `Grant "${g.title}" renewal is overdue` });
      }
    }
  });

  // Relationship lapse alerts
  contacts.forEach(c => {
    if (!c.lastContactedAt) {
      alerts.push({ type: 'info', icon: '👤', text: `${c.name} has never been contacted` });
    } else {
      const d = c.lastContactedAt.toDate ? c.lastContactedAt.toDate() : new Date(c.lastContactedAt);
      const days = Math.floor((Date.now() - d) / 86400000);
      if (days > 45) {
        alerts.push({ type: 'warning', icon: '👤', text: `No contact with ${c.name} for ${days} days` });
      }
    }
  });

  if (alerts.length === 0) return (
    <div className="bb-dn-alert bb-dn-alert--success">✅ No active alerts — all KPIs on track and relationships healthy.</div>
  );

  return (
    <div className="bb-dn-alerts-panel">
      {alerts.map((a, i) => (
        <div key={i} className={`bb-dn-alert bb-dn-alert--${a.type}`}>
          {a.icon} {a.text}
        </div>
      ))}
    </div>
  );
}

// ── Timeline event ────────────────────────────────────────────────────────────
const COMM_ICONS = { email: '✉️', call: '📞', meeting: '🤝', note: '📝' };

function TimelineEvent({ event }) {
  const icons = {
    comm:  COMM_ICONS[event.subtype] || '💬',
    grant: '📋',
  };
  const colors = { comm: '#6366F1', grant: '#F59E0B' };

  return (
    <div className="bb-dn-timeline-event">
      <div className="bb-dn-timeline-dot" style={{ background: colors[event.type] || '#6B7280' }}>
        {icons[event.type] || '•'}
      </div>
      <div className="bb-dn-timeline-content">
        <div className="bb-dn-timeline-title">{event.title}</div>
        {event.subtitle && <div className="bb-dn-timeline-subtitle">{event.subtitle}</div>}
        <div className="bb-dn-timeline-date">{event.dateStr}</div>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function DonorTimeline({ orgId }) {
  const [donors,    setDonors]    = useState([]);
  const [selectedDonorId, setSelectedDonorId] = useState('');
  const [grants,    setGrants]    = useState([]);
  const [comms,     setComms]     = useState([]);
  const [contacts,  setContacts]  = useState([]);
  const [loading,   setLoading]   = useState(false);

  // Load donors on mount
  useEffect(() => {
    getDonors(orgId).then(d => { setDonors(d); if (d.length > 0) setSelectedDonorId(d[0].id); });
  }, [orgId]);

  const load = useCallback(async () => {
    if (!selectedDonorId) return;
    setLoading(true);
    try {
      const [g, c, ct] = await Promise.all([
        getDonorGrants(orgId, selectedDonorId),
        getDonorComms(orgId, selectedDonorId),
        getDonorContacts(orgId, selectedDonorId),
      ]);
      setGrants(g); setComms(c); setContacts(ct);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [orgId, selectedDonorId]);

  useEffect(() => { load(); }, [load]);

  // Build unified timeline
  const events = [
    ...comms.map(c => ({
      type: 'comm',
      subtype: c.type,
      title: c.subject || 'Communication',
      subtitle: `${c.type} · ${c.direction}`,
      ts: c.sentAt ? new Date(c.sentAt) : (c.createdAt?.toDate?.() || new Date()),
      dateStr: c.sentAt ? fmtDateTime({ toDate: () => new Date(c.sentAt) }) : fmtDate(c.createdAt),
    })),
    ...grants.map(g => ({
      type: 'grant',
      title: `Grant: ${g.title}`,
      subtitle: `SGD ${Number(g.amount || 0).toLocaleString()} · ${g.status}`,
      ts: g.createdAt?.toDate?.() || new Date(),
      dateStr: fmtDate(g.createdAt),
    })),
  ].sort((a, b) => b.ts - a.ts);

  const selectedDonor = donors.find(d => d.id === selectedDonorId);

  return (
    <div className="bb-dn-section">
      <div className="bb-dn-section-header">
        <div>
          <h2 className="bb-dn-section-title">Activity Timeline & Alerts</h2>
          <p className="bb-dn-section-subtitle">Full activity history and live alerts for each donor</p>
        </div>
        <select
          className="bb-dn-select"
          value={selectedDonorId}
          onChange={e => setSelectedDonorId(e.target.value)}
          style={{ minWidth: 200 }}
        >
          <option value="">— Select donor —</option>
          {donors.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
      </div>

      {!selectedDonorId ? (
        <div className="bb-dn-empty">
          <div className="bb-dn-empty-icon">📊</div>
          <p>Select a donor above to view their activity timeline and alerts.</p>
        </div>
      ) : loading ? (
        <div className="bb-dn-loading"><div className="bb-loading-spinner" /><p>Loading timeline…</p></div>
      ) : (
        <>
          {/* Donor summary strip */}
          {selectedDonor && (
            <div className="bb-dn-donor-summary-strip">
              <div>
                <strong>{selectedDonor.name}</strong>
                <span className="bb-dn-muted" style={{ marginLeft: 12 }}>{selectedDonor.type}</span>
              </div>
              <div className="bb-dn-grant-meta">
                <span>📋 {grants.length} grant{grants.length !== 1 ? 's' : ''}</span>
                <span>💬 {comms.length} interaction{comms.length !== 1 ? 's' : ''}</span>
                <span>👥 {contacts.length} contact{contacts.length !== 1 ? 's' : ''}</span>
              </div>
            </div>
          )}

          {/* Alerts */}
          <h3 className="bb-dn-subsection-title">⚡ Active Alerts</h3>
          <AlertsPanel grants={grants} contacts={contacts} />

          {/* Timeline */}
          <h3 className="bb-dn-subsection-title" style={{ marginTop: 24 }}>📅 Activity Timeline</h3>
          {events.length === 0 ? (
            <div className="bb-dn-empty">
              <p>No activity recorded for this donor yet.</p>
            </div>
          ) : (
            <div className="bb-dn-timeline">
              {events.map((ev, i) => <TimelineEvent key={i} event={ev} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
